/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { Button, Modal, Table } from 'react-bootstrap-v5'
import { useParams, useHistory } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import Select from 'react-select'
import { AppContext } from '../context/AppContext'

interface RouteParams {
	id: string;
}

interface SelectOption {
	label: string;
	value: number | string;
}

interface MaterialLine {
	idItems: number;
	itemName: string;
	categoryName: string;
	quantity: number;
	unitName: string;
}

interface BomOption {
	idPackingBoms: number;
	bomName: string;
	materials: MaterialLine[];
}

interface PackSizeRow {
	idProducts: number;
	productName: string;
	productCode: string;
	packing: string;
	size: number;
	itemUnitName: string;
	packingUnit: string;
	boms: BomOption[];
	noOfPacking: string;
	removed: boolean;
}

const withItemUnit = (value: string | number, itemUnitName: string) => {
	const text = value === null || value === undefined || value === '' ? '' : String(value)
	if (!text) {
		return ''
	}
	return itemUnitName ? `${text} ${itemUnitName}` : text
}

type WarehouseField = 'packingItem' | 'finishedGoods'

/** Last warehouses picked on this page, remembered per project in this browser. */
const warehouseStorageKey = (idProjects: number) => `packingOrderCreate.warehouses.${idProjects}`

const readSavedWarehouses = (idProjects: number): Partial<Record<WarehouseField, number>> => {
	try {
		const saved = JSON.parse(localStorage.getItem(warehouseStorageKey(idProjects)) || '{}')
		return saved && typeof saved === 'object' ? saved : {}
	} catch (error) {
		return {}
	}
}

const saveWarehouse = (idProjects: number, field: WarehouseField, option: SelectOption | null) => {
	try {
		const saved = readSavedWarehouses(idProjects)
		if (option) {
			saved[field] = Number(option.value)
		} else {
			delete saved[field]
		}
		localStorage.setItem(warehouseStorageKey(idProjects), JSON.stringify(saved))
	} catch (error) {
		// storage unavailable (private mode, blocked): the page just won't remember the choice
	}
}

function PackingOrderCreate() {
	const { id }: RouteParams = useParams()
	const history = useHistory()
	const { currentUser } = useContext(AppContext)
	const canViewCost = Number(currentUser?.id_roles) === 3 || Number(currentUser?.id_roles) === 4
	const [header, setHeader] = useState<any>(null)
	const [rows, setRows] = useState<PackSizeRow[]>([])
	const [warehouses, setWarehouses] = useState<SelectOption[]>([])
	const [packingItemWarehouse, setPackingItemWarehouse] = useState<SelectOption | null>(null)
	const [finishedGoodsWarehouse, setFinishedGoodsWarehouse] = useState<SelectOption | null>(null)
	const [packingDate, setPackingDate] = useState(new Date().toISOString().slice(0, 10))
	const [saving, setSaving] = useState(false)
	const [modalRow, setModalRow] = useState<PackSizeRow | null>(null)
	const [costMap, setCostMap] = useState<Record<number, number>>({})
	const [costLoading, setCostLoading] = useState(false)
	const [costError, setCostError] = useState<string | null>(null)
	const [missingRateMap, setMissingRateMap] = useState<Record<number, Array<{ itemName: string }>>>({})

	useEffect(() => {
		if (id) {
			loadCreateData()
		}
	}, [id])

	const loadCreateData = () => {
		fetch(process.env.REACT_APP_API_URL + 'packing-orders/create-data/' + id, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				if (!resp.success) {
					Swal.fire({ icon: 'error', title: 'Error', text: resp.errorMessage || resp.errors || 'Unable to load packing data.' })
						.then(() => history.push('/work-in-progress'))
					return
				}
				setHeader(resp.data)
				setRows((resp.products || [])
					.filter((product: any) => Array.isArray(product.boms) && product.boms.length > 0)
					.map((product: any) => ({
					idProducts: product.idProducts,
					productName: product.productName,
					productCode: product.productCode,
					packing: product.packing,
					size: parseFloat(product.size) || 0,
					itemUnitName: product.itemUnitName || resp.data?.unitName || '',
					packingUnit: product.packingUnit,
					boms: product.boms || [],
					noOfPacking: '',
					removed: false,
				})))
				if (resp.data?.idProjects) {
					loadWarehouses(resp.data.idProjects)
				}
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	const loadWarehouses = (idProjects: number) => {
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options: SelectOption[] = (resp.success && Array.isArray(resp.data))
					? resp.data.map((item: any) => ({
						label: item.name,
						value: item.id_warehouses,
					}))
					: []
				setWarehouses(options)
				if (options.length > 0) {
					const saved = readSavedWarehouses(idProjects)
					const find = (idWarehouses?: number) => options.find((option) => Number(option.value) === Number(idWarehouses))
					setPackingItemWarehouse(find(saved.packingItem) || options[0])
					setFinishedGoodsWarehouse(find(saved.finishedGoods) || options[0])
				}
			})
			.catch(() => setWarehouses([]))
	}

	const visibleRows = rows.filter((row) => !row.removed)
	const visibleProductIds = visibleRows.map((row) => row.idProducts)

	const showMissingRateError = (missingItems: Array<{ itemName: string }>, message?: string) => {
		const missingNames = (missingItems || []).map((item) => item.itemName).join('<br>')
		Swal.fire({
			icon: 'error',
			title: 'Average Rate Missing',
			html: missingNames
				? `Average rate not found for the following items as of the packing date:<br><br>${missingNames}`
				: (message || 'Average rate not found for one or more packing materials.'),
		})
	}

	useEffect(() => {
		if (!header || !packingDate || visibleProductIds.length === 0) {
			setCostMap({})
			setMissingRateMap({})
			setCostError(null)
			return
		}

		let cancelled = false
		setCostLoading(true)
		setCostError(null)

		fetch(`${process.env.REACT_APP_API_URL}packing-orders/calculate-costs`, postRequestOptions({
			idBlendingOrders: Number(id),
			packingDate,
			idProducts: visibleProductIds,
		}))
			.then((resp) => resp.json())
			.then((resp) => {
				if (cancelled) {
					return
				}
				if (!resp.success) {
					const message = resp.errorMessage || resp.errors || 'Unable to calculate packing costs.'
					setCostMap({})
					setMissingRateMap({})
					setCostError(message)
					return
				}
				const nextCostMap: Record<number, number> = {}
				;(resp.costs || []).forEach((item: { idProducts: number; costPerUnit: number }) => {
					nextCostMap[item.idProducts] = Number(item.costPerUnit)
				})
				const nextMissingRateMap: Record<number, Array<{ itemName: string }>> = {}
				;(resp.missingRates || []).forEach((item: { idProducts: number; missingItems: Array<{ itemName: string }> }) => {
					nextMissingRateMap[item.idProducts] = item.missingItems || []
				})
				setCostMap(nextCostMap)
				setMissingRateMap(nextMissingRateMap)
				setCostError(null)
			})
			.catch(() => {
				if (!cancelled) {
					setCostMap({})
					setMissingRateMap({})
					setCostError('Unable to calculate packing costs.')
				}
			})
			.finally(() => {
				if (!cancelled) {
					setCostLoading(false)
				}
			})

		return () => {
			cancelled = true
		}
	}, [packingDate, header, visibleProductIds.join(',')])

	const thisOrderUsed = visibleRows.reduce((sum, row) => {
		const qty = parseFloat(row.noOfPacking)
		if (!qty || qty <= 0) {
			return sum
		}
		return sum + qty * (Number(row.size) || 0)
	}, 0)

	const remainingAfter = header ? Number(header.remainingQty) - thisOrderUsed : 0

	const selectedBom = (row: PackSizeRow) => {
		return (row.boms || [])[0] || null
	}

	const materialsToUse = useMemo(() => {
		const map: Record<number, MaterialLine> = {}
		visibleRows.forEach((row) => {
			const qty = parseFloat(row.noOfPacking)
			if (!qty || qty <= 0) {
				return
			}
			const bom = selectedBom(row)
			;(bom?.materials || []).forEach((material) => {
				if (!map[material.idItems]) {
					map[material.idItems] = { ...material, quantity: 0 }
				}
				map[material.idItems].quantity += Number(material.quantity) * qty
			})
		})
		return Object.values(map)
	}, [rows])

	const updateRow = (idProducts: number, patch: Partial<PackSizeRow>) => {
		setRows((prev) => prev.map((row) => row.idProducts === idProducts ? { ...row, ...patch } : row))
	}

	const rowsWithQty = visibleRows.filter((row) => parseFloat(row.noOfPacking) > 0 && selectedBom(row))
	const hasComputedCostsForActiveRows = rowsWithQty.every((row) => costMap[row.idProducts] !== undefined && costMap[row.idProducts] !== null)
	const missingRateItemsFor = (rowList: PackSizeRow[]) => {
		const names: Record<string, { itemName: string }> = {}
		rowList.forEach((row) => (missingRateMap[row.idProducts] || []).forEach((item) => { names[item.itemName] = item }))
		return Object.values(names)
	}
	const missingRateItemsForActiveRows = missingRateItemsFor(rowsWithQty)

	const canSave = Boolean(
		header
		&& packingItemWarehouse?.value
		&& finishedGoodsWarehouse?.value
		&& packingDate
		&& remainingAfter >= -0.00001
		&& rowsWithQty.length > 0
		&& hasComputedCostsForActiveRows
		&& !costLoading
		&& !costError
		&& !saving
	)

	const buildSavePayload = () => ({
		idBlendingOrders: Number(id),
		idPackingItemWarehouses: packingItemWarehouse?.value,
		idFinishedGoodsWarehouses: finishedGoodsWarehouse?.value,
		packingDate,
		rows: visibleRows
			.filter((row) => parseFloat(row.noOfPacking) > 0 && selectedBom(row))
			.map((row) => ({
				idProducts: row.idProducts,
				noOfPacking: Number(row.noOfPacking),
			})),
	})

	const formatShortageMessage = (shortages: Array<{ itemName: string; required: number; available: number; unitName?: string }>) => {
		const lines = shortages.map((shortage) => {
			const unit = shortage.unitName ? ` ${shortage.unitName}` : ''
			return `<li>${shortage.itemName}: Required ${parseFloat(Number(shortage.required).toFixed(5))}${unit}, Available ${parseFloat(Number(shortage.available).toFixed(5))}${unit}</li>`
		}).join('')

		return `<p>Insufficient packing material stock in the selected Packing Item Warehouse:</p><ul class="text-start mb-0">${lines}</ul>`
	}

	const handleSave = async (event: React.FormEvent) => {
		event.preventDefault()
		if (!canSave) {
			if (remainingAfter < -0.00001) {
				Swal.fire({ icon: 'warning', title: 'Validation', text: 'Packing quantity exceeds remaining blending quantity.' })
			} else if (missingRateItemsForActiveRows.length > 0) {
				showMissingRateError(missingRateItemsForActiveRows)
			} else if (costError) {
				Swal.fire({ icon: 'error', title: 'Average Rate Missing', text: costError })
			} else if (costLoading) {
				Swal.fire({ icon: 'info', title: 'Please wait', text: 'Packing costs are still being calculated.' })
			} else if (rowsWithQty.length > 0 && !hasComputedCostsForActiveRows) {
				Swal.fire({ icon: 'error', title: 'Average Rate Missing', text: 'Cost could not be calculated for one or more pack sizes.' })
			}
			return
		}

		const payload = buildSavePayload()

		try {
			const validateResponse = await fetch(
				`${process.env.REACT_APP_API_URL}packing-orders/validate-materials`,
				postRequestOptions(payload)
			)
			const validateData = await validateResponse.json()
			if (!validateData.success) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: validateData.errorMessage || validateData.errors || 'Unable to validate packing materials.',
				})
				return
			}
			if (!validateData.valid) {
				Swal.fire({
					icon: 'warning',
					title: 'Insufficient Stock',
					html: formatShortageMessage(validateData.shortages || []),
				})
				return
			}
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Unable to validate packing materials.',
			})
			return
		}

		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: 'This will stock in packed products and stock out packing materials.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, Save',
		})
		if (!confirm.isConfirmed) {
			return
		}

		setSaving(true)
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}packing-orders`, postRequestOptions(payload))
			const data = await response.json()
			if (data.success) {
				Swal.fire({
					icon: 'success',
					title: 'Success',
					text: data.successMessage,
				}).then(() => history.push('/packing-orders'))
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: data.errorMessage || data.errors || 'Save failed.',
				})
			}
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'An error occurred.',
			})
		} finally {
			setSaving(false)
		}
	}

	if (!header) {
		return (
			<div className="card card-custom">
				<div className="card-body">Loading...</div>
			</div>
		)
	}

	const modalMaterials = modalRow
		? (selectedBom(modalRow)?.materials || []).map((material) => ({
			...material,
			quantity: Number(material.quantity) * (parseFloat(modalRow.noOfPacking) > 0 ? parseFloat(modalRow.noOfPacking) : 1),
		}))
		: []

	return (
		<div className="card card-custom">
			<div className="card-header">
				<div className="card-title">
					<KTSVG path="/media/svg/icons/Text/Bullet-list.svg" className="svg-icon-1 svg-icon-primary" />
					<span className="text fw-bolder" style={{ marginLeft: '10px' }}>
						Create Packing Order
					</span>
				</div>
			</div>
			<div className="card-body">
				<form onSubmit={handleSave}>
					<Table size="sm" bordered>
						<tbody>
							<tr>
								<td width="30%">Product</td>
								<td> : {header.itemName}</td>
							</tr>
							<tr>
								<td>Product Code</td>
								<td> : {header.itemCode}</td>
							</tr>
							<tr>
								<td>Blending Order</td>
								<td> : {header.productionOrderNo}{header.batchNo ? ` (Batch No: ${header.batchNo})` : ''}</td>
							</tr>
							<tr>
								<td>Planned Production Qty</td>
								<td> : {header.productionQuantity} {header.unitName}</td>
							</tr>
							<tr>
								<td>Effective Production Qty</td>
								<td> : {header.effectiveProductionQuantity ?? header.actualProductionQuantity ?? header.productionQuantity} {header.unitName}</td>
							</tr>
							<tr>
								<td>Remaining Quantity</td>
								<td> : {header.remainingQty} {header.unitName}</td>
							</tr>
							<tr>
								<td>Packing Item Warehouse</td>
								<td>
									<Select
										value={packingItemWarehouse}
										onChange={(option) => {
										setPackingItemWarehouse(option)
										saveWarehouse(Number(header.idProjects), 'packingItem', option)
									}}
										options={warehouses}
										placeholder="Select Packing Item Warehouse"
									/>
									<div className="text-muted small mt-1">
										Packing items will be stocked out from this warehouse.
									</div>
								</td>
							</tr>
							<tr>
								<td>Finished Good Warehouse</td>
								<td>
									<Select
										value={finishedGoodsWarehouse}
										onChange={(option) => {
										setFinishedGoodsWarehouse(option)
										saveWarehouse(Number(header.idProjects), 'finishedGoods', option)
									}}
										options={warehouses}
										placeholder="Select Finished Good Warehouse"
									/>
									<div className="text-muted small mt-1">
										Finished goods will be stocked in to this warehouse.
									</div>
								</td>
							</tr>
							<tr>
								<td>Packing Date</td>
								<td>
									<input
										type="date"
										className="form-control form-control-sm"
										value={packingDate}
										onChange={(event) => setPackingDate(event.target.value)}
									/>
								</td>
							</tr>
						</tbody>
					</Table>

					<h5 className="mt-5 mb-3">Pack Size</h5>
					<Table size="sm" bordered hover>
						<thead>
							<tr>
								<th>SL</th>
								<th>Packing</th>
								<th>Size</th>
								<th>Packing Unit</th>
								<th>No of Packing</th>
								{canViewCost && <th>Cost/Per Unit</th>}
								{canViewCost && <th>Total Cost</th>}
								<th></th>
							</tr>
						</thead>
						<tbody>
							{visibleRows.length === 0 && (
								<tr>
									<td colSpan={canViewCost ? 8 : 6} className="text-center">No pack sizes found for this product.</td>
								</tr>
							)}
							{visibleRows.map((row, index) => {
								const qty = parseFloat(row.noOfPacking) || 0
								const cost = costMap[row.idProducts] ?? 0
								return (
									<tr key={row.idProducts}>
										<td>{index + 1}</td>
										<td>{withItemUnit(row.packing, row.itemUnitName)}</td>
										<td>{withItemUnit(row.size, row.itemUnitName)}</td>
										<td>{row.packingUnit}</td>
										<td>
											<input
												type="number"
												className="form-control form-control-sm"
												step="0.00001"
												min="0"
												value={row.noOfPacking}
												onChange={(event) => updateRow(row.idProducts, { noOfPacking: event.target.value })}
												onBlur={() => {
													if (qty > 0 && missingRateMap[row.idProducts]?.length) {
														showMissingRateError(missingRateMap[row.idProducts])
													}
												}}
											/>
										</td>
										{canViewCost && (
											<td>
												<input
													type="text"
													className="form-control form-control-sm"
													readOnly
													value={costMap[row.idProducts] !== undefined ? costMap[row.idProducts] : (costLoading ? '...' : '')}
												/>
												{missingRateMap[row.idProducts]?.length > 0 &&
													<div className="text-danger small">Average rate missing: {missingRateMap[row.idProducts].map((item) => item.itemName).join(', ')}</div>
												}
											</td>
										)}
										{canViewCost && (
											<td>{qty > 0 && costMap[row.idProducts] !== undefined ? parseFloat((qty * cost).toFixed(5)) : ''}</td>
										)}
										<td className="text-nowrap">
											<button type="button" className="btn btn-light-primary btn-sm me-1" onClick={() => setModalRow(row)}>
												Details
											</button>
											<button type="button" className="btn btn-light-danger btn-sm" onClick={() => updateRow(row.idProducts, { removed: true, noOfPacking: '' })}>
												X
											</button>
										</td>
									</tr>
								)
							})}
						</tbody>
					</Table>
					{costError &&
						<div className="text-danger mb-3">{costError}</div>
					}
					{remainingAfter < -0.00001 &&
						<div className="text-danger mb-3">This packing uses {parseFloat(thisOrderUsed.toFixed(5))} against remaining {header.remainingQty}.</div>
					}

					<h5 className="mt-5 mb-3">Material to Use</h5>
					<Table size="sm" bordered hover>
						<thead>
							<tr>
								<th>SL</th>
								<th>Item</th>
								<th>Category</th>
								<th>Quantity</th>
								<th>Unit</th>
							</tr>
						</thead>
						<tbody>
							{materialsToUse.length === 0 && (
								<tr>
									<td colSpan={5} className="text-center">Enter packing quantity to see materials.</td>
								</tr>
							)}
							{materialsToUse.map((material, index) => (
								<tr key={material.idItems}>
									<td>{index + 1}</td>
									<td>{material.itemName}</td>
									<td>{material.categoryName}</td>
									<td>{parseFloat(Number(material.quantity).toFixed(5))}</td>
									<td>{material.unitName}</td>
								</tr>
							))}
						</tbody>
					</Table>

					<div className="form-group row mt-4">
						<div className="col text-center">
							<button type="submit" className="btn btn-sm btn-primary me-2" disabled={!canSave}>
								Save
							</button>
							<Button variant="secondary" size="sm" onClick={() => history.push('/work-in-progress')}>
								Close
							</Button>
						</div>
					</div>
				</form>
			</div>

			<Modal show={!!modalRow} onHide={() => setModalRow(null)} size="lg">
				<Modal.Header closeButton>
					<Modal.Title>Packing Materials</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Table size="sm" bordered>
						<thead>
							<tr>
								<th>SL</th>
								<th>Item</th>
								<th>Category</th>
								<th>Quantity</th>
								<th>Unit</th>
							</tr>
						</thead>
						<tbody>
							{modalMaterials.length === 0 && (
								<tr>
									<td colSpan={5} className="text-center">No packing materials for this BOM.</td>
								</tr>
							)}
							{modalMaterials.map((material, index) => (
								<tr key={material.idItems}>
									<td>{index + 1}</td>
									<td>{material.itemName}</td>
									<td>{material.categoryName}</td>
									<td>{parseFloat(Number(material.quantity).toFixed(5))}</td>
									<td>{material.unitName}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Modal.Body>
			</Modal>
		</div>
	)
}

export default PackingOrderCreate
